import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Method,
  JSX,
  Watch
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";
import { Locale } from "../../../common/locale";
import {
  ChCheckboxCustomEvent,
  ChEditCustomEvent,
  ComboBoxModel,
  TabularGridColumnSelectorClickedEvent,
  TabularGridColumnSortChangedEvent,
  TabularGridMarkingChangedEvent,
  TabularGridRowContextMenuEvent
} from "@genexus/chameleon-controls-library";

import uncheckIgnoredObjects from "../version-control/common/uncheck-ignored";
import { renderObjectTypeWithIcon } from "../common/utilities";
import { formatDate } from "../../../common/helpers";
import {
  ContextMenuInfo,
  ItemsCheckedResult,
  ItemsCheckedState,
  ObjectType
} from "../../../common/types";
import { config } from "../../../common/config";
import { mapObjectTypeToComboBoxItemModel } from "../version-control/common/object-type-to-combo-box";
import { COLUMNS_IDS } from "../version-control/common/tabular-grid-render";
import sortTabularGridColumn, {
  type SortTabularGridColumnDataType
} from "../../../common/chameleon/tabular-grid-column-sort";
import areSetsEqual from "../version-control/common/are-sets-equal";
import {
  Action,
  ActionForFilter,
  LoadRemoteChangesCallback,
  ModifiedObject,
  UpdateCallback,
  UpdateData,
  UpdateFiltersData,
  UpdateStatus,
  UpdateStatusInternal
} from "../version-control/common/types";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tabular-grid",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const CHECKBOX_CHECKED_VALUE = "true";
const CHECKBOX_UNCHECKED_VALUE = "false";

const DETAIL_LABEL_CLASSES = "detail__label body-semi-bold-s";

// icons
const ADD_ICON = getIconPath({
  category: "system",
  name: "add",
  colorType: "on-surface"
});
const EDITED_ICON = getIconPath({
  category: "system",
  name: "edit",
  colorType: "on-surface"
});
const FILTER_ICON = getIconPath({
  category: "window-tools",
  name: "filter",
  colorType: "on-surface"
});
const SEARCH_ICON = getIconPath({
  category: "system",
  name: "search",
  colorType: "on-surface"
});
const SUBSTRACT_ICON = getIconPath({
  category: "system",
  name: "substract",
  colorType: "on-surface"
});
const WARNING_ICON = getIconPath({
  category: "system",
  name: "warning",
  colorType: "warning"
});
const ERROR_ICON = getIconPath({
  category: "system",
  name: "error",
  colorType: "error"
});
const SUCCESS_ICON = getIconPath({
  category: "system",
  name: "check ",
  colorType: "success"
});
const INFO_ICON = getIconPath({
  category: "system",
  name: "information",
  colorType: "on-surface"
});
const REFRESH_ICON = getIconPath({
  category: "gemini-tools",
  name: "reset",
  colorType: "interactive"
});

@Component({
  tag: "gx-ide-team-dev-update",
  styleUrl: "update.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/team-dev-update"]
})
export class GxIdeTeamDevUpdate {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #actionsComboBoxModel: { caption: string; value: ActionForFilter }[];

  /**
   * Used to determine if pendingItemsCheckedCallback callback has to be called or not.
   * It should be ignored if the grid checkboxes state is changed programmatically, by
   * setting markRow to false once the object has been successfully updated, for example.
   */
  #rowCheckedChangedByTheUser: boolean = true;

  @Element() el: HTMLGxIdeTeamDevBringChangesElement;

  #chGridPendingForUpdateEl!: HTMLChTabularGridElement;

  // - - - - - - - - - - - - -
  // States Bring Changes
  // - - - - - - - - - - - - -

  /**
   * It hols a boolean indicating if at least one object from the tabular-grid is checked, for merging.
   */
  @State() atLeastOneObjectIsChecked: boolean = false;

  /**
   * True while commitObjects hasn't been loaded or is empty. Used to display a loader.
   */
  @State() loading: boolean = true;

  /**
   * The current search filter value
   */
  @State() menuContextAction: Action;

  /**
   * The current search filter value
   */
  @State() searchValue: string = "";

  /**
   * The current state filter value
   */
  @State() selectedAction: ActionForFilter = "all-actions";

  /**
   * A set that stores the marked state of each object cell. This is necessary because filtering the grid * causes marked cells to lose their state.
   */
  @State() markedObjectsSet: Set<string> = new Set<string>([]);
  @Watch("markedObjectsSet")
  markedObjectsSetChanged(newMarkedObjectsSet: Set<string>) {
    this.atLeastOneObjectIsChecked = newMarkedObjectsSet.size > 0;
  }

  /**
   * A set that holds the id's of the objects that have been updated already.
   */
  @State() alreadyUpdatedObjectsIdsSet: Set<string> = new Set<string>([]);
  @Watch("alreadyUpdatedObjectsIdsSet")
  alreadyUpdatedObjectsIdsSetChanged(newUpdatedObjectsIdsSet: Set<string>) {
    const pendingIdsSetAndUpdatedIdsSetAreEqual = areSetsEqual(
      newUpdatedObjectsIdsSet,
      this.pendingIdsSet
    );

    this.allPendingAreUpdated =
      pendingIdsSetAndUpdatedIdsSetAreEqual && this.pendingIdsSet.size > 0;
  }

  /**
   * A set that holds the id's of all the pending objects. Used to quickly determine if the selected rows belong
   * to "pending" or not. This information is required for the ContextMenuCallback callback. This set has to be updated
   * on every loadCallback.
   */
  @State() pendingIdsSet: Set<string> = new Set<string>([]);

  /**
   * A set that holds the id's of all the ignored objects. Used to quickly determine if the selected rows belong
   * to "ignored" or not. This information is required for the ContextMenuCallback callback. This set has to be updated
   * on every loadCallback.
   */
  @State() ignoredIdsSet: Set<string> = new Set<string>([]);

  /**
   * A map that holds object messages, after trying to update ('info' | ‘success’ | 'warning' | 'error')
   */
  @State() objectsMessagesDataMap: Map<string, MessageData[]> = new Map();

  /**
   * Set to true when objects are being mered. This helps disabling controls until all objects are done.
   */
  @State() updating: boolean = false;

  // - - - - - - - - - - - - -
  // State Update
  // - - - - - - - - - - - - -

  /**
   * A boolean that indicates if all the pending objects, have been updated
   * already.
   */
  @State() allPendingAreUpdated: boolean = false;

  /**
   * The "KB Properties" checkbox value
   */
  @State() kbPropertiesChecked: boolean = false;

  /**
   * The combobox model for the types filter.
   */
  @State() typesComboBoxModel: ComboBoxModel;

  /**
   * The selected type
   */
  @State() selectedType: string;

  /**
   * Indicates if the update button should be disabled
   */
  @State() isUpdateDisabled: boolean = false;

  /**
   * A map that holds the current update objects states ("inserted" | "modified" | "modified-with-conflicts" | "deleted" | "updating" | "update")
   */
  @State() objectsStatusMap: Map<string, UpdateStatusInternal> = new Map();
  @Watch("objectsStatusMap")
  objectsStatusMapChanged(
    newObjectsStatusMap: Map<string, UpdateStatusInternal>
  ) {
    const someObjectIsUpdating = [...newObjectsStatusMap.entries()].find(
      ([, state]) => state === "updating"
    );

    if (!someObjectIsUpdating) {
      this.updating = false;
    }
  }

  // - - - - - - - - - - - - -
  // Properties Bring Changes
  // - - - - - - - - - - - - -

  /**
   * Callback invoked when user activates the context menu on any of the grids.
   */
  @Prop() readonly contextMenuCallback!: ContextMenuCallback;

  /**
   * All objects ("pending for update" and "ignored objects") before "state" filter
   */
  @State() pendingAndIgnored: UpdateData = {
    pendingForUpdate: [],
    ignoredObjects: []
  };
  @Watch("pendingAndIgnored")
  pendingAndIgnoredChanged() {
    this.pendingIdsSet.clear(); // before pendingIdsSet.add
    this.ignoredIdsSet.clear(); // before pendingIdsSet.add
    this.objectsMessagesDataMap = new Map();
    this.alreadyUpdatedObjectsIdsSet = new Set();
    this.markedObjectsSet = new Set(); // before #markPendingObjects

    this.pendingAndIgnored.pendingForUpdate.forEach(pending => {
      this.pendingIdsSet.add(pending.id);
    });

    this.pendingAndIgnored.ignoredObjects.forEach(ignored => {
      this.ignoredIdsSet.add(ignored.id);
    });

    this.#markPendingObjects(); // after pendingIdsSet.add and ignoredIdsSet.add
    this.#setInitialObjectsStatus(); // after pendingIdsSet.add and ignoredIdsSet.add

    this.#applyClientFilter();
  }

  /**
   * Pending objects after "action" filter
   */
  @State() pendingObjectsAfterActionFilter: ModifiedObject[] = [];

  /**
   * Ignored objects after "action" filter
   */
  @State() ignoredObjectsAfterActionFilter: ModifiedObject[] = [];

  /**
   * Callback invoked to return the list of data types loaded from file or url.
   */
  @Prop() readonly loadCallback!: LoadRemoteChangesCallback;

  /**
   * Callback invoked to return information of a Font type of data.
   */
  @Prop() readonly pendingItemsCheckedCallback!: PendingItemsCheckedCallback;

  /**
   * Possible values for type filter
   */
  @Prop() readonly types: ObjectType[];

  /**
   * If true, the "Action" column will display a button instead of a pill, only when the action is
   */
  @Prop() readonly updatePillAsButton: boolean = false;

  // - - - - - - - - - - - - -
  // Properties Update
  // - - - - - - - - - - - - -

  /**
   * Callback invoked when the update process is to be started (’Update’ button).
   */
  @Prop() readonly updateCallback!: UpdateCallback;

  /**
   * Callback invoked when user clicks in server url of Update From group.
   */
  @Prop() readonly openServerCallback!: () => Promise<void>;

  /**
   * Information to show in Update From group.
   */
  @Prop() readonly updateFrom: UpdateFromData;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);

    // Initiate "actions" combo box filter
    this.#actionsComboBoxModel = [
      {
        caption: this.#componentLocale.actions.allActions,
        value: "all-actions"
      },
      {
        caption: this.#componentLocale.actions.insert,
        value: "inserted"
      },
      {
        caption: this.#componentLocale.actions.modify,
        value: "modified"
      },
      {
        caption: this.#componentLocale.actions.modifyWithConflicts,
        value: "modified-with-conflicts"
      },
      {
        caption: this.#componentLocale.actions.delete,
        value: "deleted"
      }
    ];

    // Set type initial value
    this.selectedType = this.types[0]?.id;

    this.typesComboBoxModel = mapObjectTypeToComboBoxItemModel(this.types);
  }

  async componentDidLoad() {
    await this.#loadData(true);

    // initialize "allPendingAreUpdated"
    this.alreadyUpdatedObjectsIdsSetChanged(this.alreadyUpdatedObjectsIdsSet);
  }

  /**
   * Used by the host to instruct the component to force a data reload in the grids.
   */
  @Method()
  async reload() {
    await this.#loadData(true);
  }

  /**
   * Used by the host to instruct the component to disable the update button.
   */
  @Method()
  async disableUpdate(value: boolean) {
    this.isUpdateDisabled = value;
  }

  #updateCheckedClickedHandler = async () => {
    this.#updateObjectsHandler([...this.markedObjectsSet]);
  };

  #reloadClickedHandler = async () => {
    this.#loadData(true);
  };

  #updateObjectsHandler = async (selection: string[]) => {
    this.updating = true;

    selection.forEach(itemToUpdate => {
      this.objectsStatusMap.set(itemToUpdate, "updating");
    });

    const updateResult = await this.updateCallback(selection);
    updateResult.forEach(async objectResult => {
      const messages = objectResult.messages;
      if (messages.length) {
        this.objectsMessagesDataMap.set(objectResult.id, objectResult.messages);
      } else {
        // clear messages
        this.objectsMessagesDataMap.delete(objectResult.id);
      }
      if (objectResult.result === "updated") {
        this.alreadyUpdatedObjectsIdsSet = new Set(
          this.alreadyUpdatedObjectsIdsSet
        ).add(objectResult.id);

        this.objectsStatusMap.set(objectResult.id, "updated");
        await this.#chGridPendingForUpdateEl.markRow(objectResult.id, false);
      } else {
        this.objectsStatusMap.set(objectResult.id, "pending");
      }
    });
    this.updating = false;
  };

  #renderActionCellContent = (
    action: Action
  ): JSX.Element[] | null | string => {
    let icon: string;
    let caption: string;
    let iconColor: string;

    if (action === "deleted") {
      icon = SUBSTRACT_ICON;
      caption = this.#componentLocale.actions.delete;
      iconColor = "--color-icon-error-default";
    } else if (action === "inserted") {
      icon = ADD_ICON;
      caption = this.#componentLocale.actions.insert;
      iconColor = "--color-icon-success-default";
    } else if (action === "modified") {
      icon = EDITED_ICON;
      caption = this.#componentLocale.actions.modify;
      iconColor = "--color-icon-warning-default";
    } else if (action === "modified-with-conflicts") {
      icon = WARNING_ICON;
      caption = this.#componentLocale.actions.modifyWithConflicts;
      iconColor = "--color-icon-warning-default";
    } else if (!action) {
      // proably is "ignored"
      return null;
    }

    return (
      <span class="custom-icon-text-wrapper">
        <ch-image
          type="mask"
          class="icon-s"
          src={icon}
          style={{ backgroundColor: `var(${iconColor})` }}
        ></ch-image>
        {`${caption}`}
      </span>
    );
  };

  #updateSingleObject: (objectId: string) => () => void =
    objectId => async () => {
      const newobjectsStatusMap = new Map(this.objectsStatusMap);
      newobjectsStatusMap.set(objectId, "updating");
      this.objectsStatusMap = newobjectsStatusMap;
      this.#updateObjectsHandler([objectId]);
    };

  #renderObjectStatus = (objectId: string): JSX.Element[] => {
    const status = this.objectsStatusMap.get(objectId);

    const caption =
      status === "pending" && this.updatePillAsButton
        ? this.#componentLocale.status.update
        : this.#componentLocale.status[status];
    if (status === "updating") {
      return <span class="updating-spinner-caption">{caption}</span>;
    }
    const classes = `pill pill--${status}`;
    if (this.updatePillAsButton && status === "pending") {
      return (
        <button
          class="button-secondary pill pill--update"
          onClick={this.#updateSingleObject(objectId)}
        >
          {caption}
        </button>
      );
    } else {
      return <span class={classes}>{caption}</span>;
    }
  };

  #renderRows = (type: UpdateSourceType): JSX.Element[] => {
    let objects: ModifiedObject[];
    if (type === "pending") {
      objects = this.pendingObjectsAfterActionFilter;
    } else if (type === "ignored") {
      objects = this.ignoredObjectsAfterActionFilter;
    }

    return objects.map(object => {
      const objectType: ObjectType = {
        id: object.id,
        icon: object.typeIcon,
        name: object.type
      };

      const alreadyUpdated = this.alreadyUpdatedObjectsIdsSet.has(object.id);
      const hideCheckbox = alreadyUpdated || type === "ignored";

      const marked = this.markedObjectsSet.has(object.id);

      const messages = this.objectsMessagesDataMap.get(object.id);

      return (
        <ch-tabular-grid-row
          class="tabular-grid-row"
          key={object.id}
          rowid={object.id}
          ref={(el: HTMLElement) =>
            marked && el?.setAttribute("marked", "true")
          }
        >
          <ch-tabular-grid-cell
            cell-type="rich"
            row-selector="true"
            class={{
              "tabular-grid-cell": true,
              "no-checkbox-custom-selector": hideCheckbox
            }}
            size="max-content"
          >
            {object.name}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {renderObjectTypeWithIcon(objectType)}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {object.description}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {formatDate(object.modifiedOn)}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {this.#renderActionCellContent(object.action)}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell tabular-grid-cell--for-pill">
            {this.#renderObjectStatus(object.id)}
          </ch-tabular-grid-cell>

          {messages?.length && (
            <ch-tabular-grid-rowset class="tabular-grid-rowset">
              <ch-tabular-grid-rowset-empty
                class={{
                  "tabular-grid-rowset-empty": true
                }}
              >
                <ul class="messages-list">
                  {messages.map(message => {
                    let MESSAGE_ICON;
                    if (message.type === "error") {
                      MESSAGE_ICON = ERROR_ICON;
                    } else if (message.type === "warning") {
                      MESSAGE_ICON = WARNING_ICON;
                    } else if (message.type === "success") {
                      MESSAGE_ICON = SUCCESS_ICON;
                    } else {
                      // info
                      MESSAGE_ICON = INFO_ICON;
                    }

                    return (
                      <li class="message-description">
                        <ch-image
                          class="icon-s message-icon"
                          src={MESSAGE_ICON}
                        ></ch-image>
                        {message.text}
                      </li>
                    );
                  })}
                </ul>
              </ch-tabular-grid-rowset-empty>
            </ch-tabular-grid-rowset>
          )}
        </ch-tabular-grid-row>
      );
    });
  };

  #rowMarkingChangedHandler = async (
    event: CustomEvent<TabularGridMarkingChangedEvent>
  ) => {
    const markedRowsIds = event.detail.rowsId;
    this.markedObjectsSet = new Set(
      markedRowsIds.filter(
        markedObjectId => !this.alreadyUpdatedObjectsIdsSet.has(markedObjectId)
      )
    );

    if (this.#rowCheckedChangedByTheUser) {
      this.#updatePendingCheckedItems(event);
    }
  };

  #updatePendingCheckedItems = async (
    event: CustomEvent<TabularGridMarkingChangedEvent>
  ) => {
    this.#rowCheckedChangedByTheUser = false;
    const addedRowsIds = event.detail.addedRowsId;
    const removedRowsId = event.detail.removedRowsId;
    try {
      const pendingItemCheckedResult = await this.pendingItemsCheckedCallback({
        itemsChecked: addedRowsIds,
        itemsUnchecked: removedRowsId
      });

      if (pendingItemCheckedResult === undefined) {
        // undo check/uncheck
        await this.#revertPendingCommitsCheckboxes(addedRowsIds, removedRowsId);
      } else {
        await this.#togglePendingCommitsCheckboxes(pendingItemCheckedResult);
      }
    } catch {
      //catch exception if necessary
    } finally {
      this.#rowCheckedChangedByTheUser = true;
    }
  };

  #searchInputHandler = (event: ChEditCustomEvent<string> | InputEvent) => {
    this.searchValue = event.detail as string;
    this.#loadData();
  };

  // Local Methods Update

  #actionChangedHandler = (event: CustomEvent<string> | InputEvent) => {
    this.selectedAction = event.detail as ActionForFilter;
    this.#loadData();
  };

  #typeChangedHandler = async (event: CustomEvent<string> | InputEvent) => {
    this.selectedType = event.detail as string;
    this.#loadData();
  };

  #setInitialObjectsStatus = (): void => {
    this.pendingAndIgnored.pendingForUpdate.forEach(pendingObject => {
      const status: UpdateStatusInternal =
        (pendingObject.status as UpdateStatus) ?? "pending";

      this.objectsStatusMap.set(pendingObject.id, status);
      if (status === "updated") {
        this.alreadyUpdatedObjectsIdsSet.add(pendingObject.id);
      }
    });
    this.pendingAndIgnored.ignoredObjects.forEach(ignoredObject => {
      const status: UpdateStatusInternal =
        (ignoredObject.status as UpdateStatus) ?? "ignored";
      this.objectsStatusMap.set(ignoredObject.id, status);
    });
  };

  #loadData = async (forceLoad: boolean = false): Promise<void> => {
    const filters: UpdateFiltersData = {
      search: this.searchValue,
      type: this.selectedType,
      kbProperties: this.kbPropertiesChecked,
      action: this.selectedAction
    };

    try {
      this.loading = true;
      this.objectsMessagesDataMap.clear();

      this.pendingAndIgnored = await this.loadCallback(filters, forceLoad);
      this.#setInitialObjectsStatus();

      // Is is required to mark/check all objects after loadCallback.
      this.#markPendingObjects();
    } catch (error) {
      // Error handling for update operation
    } finally {
      this.loading = false;
    }
  };

  /**
   * Marks all pending objects that have not been committed yet.
   *
   * Iterates through all pending object IDs and adds them to the marked objects set
   * if they are not already in the committed objects set. This effectively identifies
   * and marks all the pending objects that still need to be processed.
   **/
  #markPendingObjects = () => {
    for (const pendingId of this.pendingIdsSet) {
      if (!this.alreadyUpdatedObjectsIdsSet.has(pendingId)) {
        this.markedObjectsSet = new Set([...this.markedObjectsSet, pendingId]);
      }
    }
  };

  #applyClientFilter = () => {
    // this.pendingObjectsAfterActionFilter = this.#filteredObjects(
    //   this.pendingAndIgnored.pendingForUpdate
    // );
    this.pendingObjectsAfterActionFilter =
      this.pendingAndIgnored.pendingForUpdate;
    // this.ignoredObjectsAfterActionFilter = this.#filteredObjects(
    //   this.pendingAndIgnored.ignoredObjects
    // );
    this.ignoredObjectsAfterActionFilter =
      this.pendingAndIgnored.ignoredObjects;
  };

  // #filteredObjects = (objects: ModifiedObject[]): ModifiedObject[] => {
  //   let filtered = objects;

  //   // name or description
  //   if (this.searchValue.length) {
  //     filtered = filtered.filter(object => {
  //       const nameFiltered = object.name
  //         .toLowerCase()
  //         .includes(this.searchValue.toLowerCase());

  //       const descriptionFiltered = object.description
  //         .toLowerCase()
  //         .includes(this.searchValue.toLowerCase());

  //       return nameFiltered || descriptionFiltered;
  //     });
  //   }

  //   return filtered;
  // };

  #onRowContextMenuHandler =
    () => async (ev: CustomEvent<TabularGridRowContextMenuEvent>) => {
      ev.preventDefault();
      ev.stopPropagation();

      const selection = await this.#chGridPendingForUpdateEl.getSelectedRows();

      const selectedRowId = (
        await this.#chGridPendingForUpdateEl.getSelectedCell()
      ).rowId;

      const source: UpdateSourceType = this.pendingIdsSet.has(selectedRowId)
        ? "pending"
        : "ignored";

      let filteredSelection: string[] = [];
      // remove selected rows from the other source, if any.
      if (selection.length > 1 && source === "pending") {
        selection.forEach(async selectedRowId => {
          if (this.ignoredIdsSet.has(selectedRowId)) {
            await this.#chGridPendingForUpdateEl.selectRow(
              selectedRowId,
              false
            );
          } else {
            filteredSelection.push(selectedRowId);
          }
        });
      } else if (selection.length > 1 && source === "ignored") {
        selection.forEach(async selectedRowId => {
          if (this.pendingIdsSet.has(selectedRowId)) {
            await this.#chGridPendingForUpdateEl.selectRow(
              selectedRowId,
              false
            );
          } else {
            filteredSelection.push(selectedRowId);
          }
        });
      } else {
        filteredSelection = selection;
      }

      await this.contextMenuCallback(source, {
        selection: filteredSelection,
        clientX: ev.detail.clientX,
        clientY: ev.detail.clientY
      });
    };

  #revertPendingCommitsCheckboxes = async (
    addedRowsIds: string[],
    removedRowsId: string[]
  ) => {
    if (this.#chGridPendingForUpdateEl) {
      for (const rowId of addedRowsIds) {
        await this.#chGridPendingForUpdateEl.markRow(rowId, false);
      }
      for (const rowId of removedRowsId) {
        await this.#chGridPendingForUpdateEl.markRow(rowId, true);
      }
    }
  };

  #togglePendingCommitsCheckboxes = async (
    itemsToToggle: ItemsCheckedResult
  ) => {
    if (this.#chGridPendingForUpdateEl) {
      const itemsToCheck: string[] = itemsToToggle.itemsToCheck;
      const itemsToUncheck: string[] = itemsToToggle.itemsToUncheck;
      // check
      for (const rowId of itemsToCheck) {
        await this.#chGridPendingForUpdateEl.markRow(rowId, true);
      }
      // uncheck
      for (const rowId of itemsToUncheck) {
        await this.#chGridPendingForUpdateEl.markRow(rowId, false);
      }
    }
  };

  #kbPropertiesChangedHandler = (
    event: ChCheckboxCustomEvent<any> | InputEvent
  ) => {
    this.kbPropertiesChecked = event.detail === CHECKBOX_CHECKED_VALUE;
    this.#loadData(true);
  };

  #sortTabularGridColumnCallback = (
    e: CustomEvent<TabularGridColumnSortChangedEvent>
  ) => {
    const columnId = e.detail.columnId;

    let objectProperty;
    let dateType: SortTabularGridColumnDataType = "string";
    if (columnId === COLUMNS_IDS.lastSynchronized) {
      dateType = "dateString";
    }

    this.pendingObjectsAfterActionFilter = [
      ...sortTabularGridColumn(
        e,
        this.pendingObjectsAfterActionFilter,
        dateType,
        objectProperty
      )
    ];
    this.ignoredObjectsAfterActionFilter = [
      ...sortTabularGridColumn(
        e,
        this.ignoredObjectsAfterActionFilter,
        dateType,
        objectProperty
      )
    ];
  };

  /**
   * WA: Ignored objects must always remain unchecked.
   * Because `tabular-grid` selects all rows when the checkbox column is toggled,
   * we need to manually unselect the ignored IDs. // TODO: Move this function
   * to common
   */
  #columnSelectorClickedCallback = async (
    e: CustomEvent<TabularGridColumnSelectorClickedEvent>
  ) => {
    const allRowsChecked = e.detail;
    if (allRowsChecked) {
      const markedRowsIds =
        await this.#chGridPendingForUpdateEl.getMarkedRows();
      const ignoredMarkedRowsIds = markedRowsIds.filter(rowId => {
        return this.ignoredIdsSet.has(rowId);
      });
      if (ignoredMarkedRowsIds.length) {
        uncheckIgnoredObjects(
          ignoredMarkedRowsIds,
          this.#chGridPendingForUpdateEl
        );
      }
    }
  };

  render() {
    const disableControls =
      this.updating || this.loading || this.isUpdateDisabled;
    const gridIsEmpty =
      this.pendingObjectsAfterActionFilter.length === 0 &&
      this.ignoredObjectsAfterActionFilter.length === 0;

    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          <header class="header spacing-body-inline spacing-body-block-start">
            <div
              // header details
              class="header__detail header__detail-1"
            >
              <p class="detail__heading-genexus-server subtitle-semi-bold-s">
                {this.#componentLocale.commitDetail.title}
              </p>
              <div
                // genexus server
                class="detail__genexus-server field field-block"
              >
                <span class={DETAIL_LABEL_CLASSES}>
                  {this.#componentLocale.commitDetail.genexusServer}
                </span>
                <button
                  class="detail__link body-regular-s"
                  onClick={this.openServerCallback}
                >
                  {this.updateFrom ? this.updateFrom.serverUrl : ""}
                </button>
              </div>

              <div
                // knowledge base
                class="detail__knowledge-base field field-block"
              >
                <span class={DETAIL_LABEL_CLASSES}>
                  {this.#componentLocale.commitDetail.knowledgeBase}
                </span>
                <p class="body-regular-s">
                  {this.updateFrom ? this.updateFrom.kbName : ""}
                </p>
              </div>

              <div
                // version
                class="detail__version field field-block"
              >
                <span class={DETAIL_LABEL_CLASSES}>
                  {this.#componentLocale.commitDetail.version}
                </span>
                <p class="body-regular-s">
                  {this.updateFrom ? this.updateFrom.versionName : ""}
                </p>
              </div>

              <div
                // last full update
                class="detail__last-full-update field field-block"
              >
                <span class={DETAIL_LABEL_CLASSES}>
                  {this.#componentLocale.commitDetail.lastFullUpdate}
                </span>
                <p class="body-regular-s">
                  {formatDate(this.updateFrom.lastFullUpdate)}
                </p>
              </div>
            </div>
          </header>

          <div class="filter-container spacing-body-inline">
            <div class="filter field-group">
              <ch-edit
                // search
                class="input filter__search"
                disabled={disableControls}
                startImgSrc={SEARCH_ICON}
                accessibleName={
                  this.#componentLocale.filter.searchAccessibleName
                }
                placeholder={this.#componentLocale.filter.searchPlaceholder}
                type="search"
                onInput={this.#searchInputHandler}
                debounce={config.inputDebounce}
              ></ch-edit>

              <ch-combo-box-render
                // types
                class="combo-box filter__types"
                disabled={disableControls}
                model={this.typesComboBoxModel}
                value={this.selectedType}
                accessibleName={this.#componentLocale.filter.typeAccesibleName}
                placeholder={this.#componentLocale.filter.typePlaceholder}
                onInput={this.#typeChangedHandler}
              ></ch-combo-box-render>

              <ch-combo-box-render
                // actions
                class="combo-box filter__actions"
                disabled={disableControls}
                model={this.#actionsComboBoxModel}
                value={this.selectedAction}
                onInput={this.#actionChangedHandler}
                accessibleName={
                  this.#componentLocale.filter.actionAccesibleName
                }
                placeholder={this.#componentLocale.filter.actionPlaceholder}
              ></ch-combo-box-render>

              <ch-checkbox
                // kb properties
                class="checkbox filter__kb-properties"
                caption={this.#componentLocale.filter.kbProperties}
                checkedValue={CHECKBOX_CHECKED_VALUE}
                value={CHECKBOX_UNCHECKED_VALUE}
                onInput={this.#kbPropertiesChangedHandler}
                disabled={disableControls}
              />

              <div class="buttons-spacer filter__buttons">
                <button
                  // reload button
                  class="button-secondary button-icon-and-text"
                  disabled={disableControls}
                  onClick={this.#reloadClickedHandler}
                >
                  <ch-image class="icon-m" src={REFRESH_ICON}></ch-image>
                  {this.#componentLocale.filter.reload}
                </button>
                <button
                  // update button
                  class="button-primary button-update"
                  disabled={!this.atLeastOneObjectIsChecked || disableControls}
                  onClick={this.#updateCheckedClickedHandler}
                >
                  {this.#componentLocale.filter.update}
                </button>
              </div>
            </div>
          </div>

          <div class="main spacing-body">
            {!this.loading ? (
              <ch-tabular-grid
                class={{
                  "tabular-grid": true,
                  "objects-tabular-grid": true,
                  "disabled-custom-selector": this.updating,
                  "empty-result": gridIsEmpty
                }}
                keyboardNavigationMode="focus"
                rowSelectionMode="multiple"
                onRowMarkingChanged={this.#rowMarkingChangedHandler}
                onRowContextMenu={this.#onRowContextMenuHandler()}
                ref={(el: HTMLChTabularGridElement) =>
                  (this.#chGridPendingForUpdateEl = el)
                }
              >
                <ch-tabular-grid-columnset class="tabular-grid-column-set">
                  <ch-tabular-grid-column
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.name}
                    columnName={this.#componentLocale.objects.name}
                    columnType="rich"
                    richRowSelector={!this.allPendingAreUpdated}
                    richRowSelectorMode="mark"
                    settingable={false}
                    sortable={true}
                    onColumnSortChanged={this.#sortTabularGridColumnCallback}
                    onColumnSelectorClicked={
                      this.#columnSelectorClickedCallback
                    }
                    size="max-content"
                  />
                  <ch-tabular-grid-column
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.type}
                    columnName={this.#componentLocale.objects.type}
                    settingable={false}
                    sortable={true}
                    onColumnSortChanged={this.#sortTabularGridColumnCallback}
                    size="max-content"
                  />
                  <ch-tabular-grid-column
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.description}
                    columnName={this.#componentLocale.objects.description}
                    settingable={false}
                    sortable={true}
                    onColumnSortChanged={this.#sortTabularGridColumnCallback}
                    size={config.tabularGrid.colSize.description}
                  />
                  <ch-tabular-grid-column
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.modifiedOn}
                    columnName={this.#componentLocale.objects.modifiedOn}
                    settingable={false}
                    sortable={true}
                    onColumnSortChanged={this.#sortTabularGridColumnCallback}
                    size="max-content"
                  />
                  <ch-tabular-grid-column
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.action}
                    columnName={this.#componentLocale.objects.action}
                    settingable={false}
                    sortable={false}
                    size="max-content"
                  />
                  <ch-tabular-grid-column
                    // size of this column should the enough for the largest pill, and no more.
                    // this prevents layout shifts when pills state changes.
                    class="tabular-grid-column"
                    columnId={COLUMNS_IDS.status}
                    columnName={this.#componentLocale.objects.status}
                    settingable={false}
                    sortable={false}
                    size="100px"
                  />
                </ch-tabular-grid-columnset>

                {!gridIsEmpty
                  ? [
                      <ch-tabular-grid-rowset class="tabular-grid-rowset">
                        <ch-tabular-grid-rowset-legend class="tabular-grid-rowset-legend">
                          {this.#componentLocale.objects.pending}
                        </ch-tabular-grid-rowset-legend>
                        {this.#renderRows("pending")}
                      </ch-tabular-grid-rowset>,
                      <ch-tabular-grid-rowset class="tabular-grid-rowset">
                        <ch-tabular-grid-rowset-legend class="tabular-grid-rowset-legend">
                          {this.#componentLocale.objects.ignored}
                        </ch-tabular-grid-rowset-legend>
                        {this.#renderRows("ignored")}
                      </ch-tabular-grid-rowset>
                    ]
                  : null}

                <ch-tabular-grid-rowset class="tabular-grid-rowset">
                  {gridIsEmpty && (
                    <ch-tabular-grid-rowset-empty>
                      <gx-ide-empty-state
                        class="empty-state"
                        isAnimated
                        stateIconSrc={FILTER_ICON}
                        stateTitle="No object matched your filter"
                      ></gx-ide-empty-state>
                    </ch-tabular-grid-rowset-empty>
                  )}
                </ch-tabular-grid-rowset>
              </ch-tabular-grid>
            ) : (
              <gx-ide-loader
                cancelLabel={this.#componentLocale.loader.cancelLabel}
                loaderTitle={this.#componentLocale.loader.title}
                description={this.#componentLocale.loader.description}
                show
                class="loader"
              ></gx-ide-loader>
            )}
          </div>
        </section>
      </Host>
    );
  }
}

// - - - - - - - - - - - - -
// Types from Update
// - - - - - - - - - - - - -

export type ContextMenuCallback = (
  source: UpdateSourceType,
  contextMenuInfo: ContextMenuInfo
) => Promise<void>;

export type PendingItemsCheckedCallback = (
  pendingItemsCheckedState: ItemsCheckedState
) => Promise<ItemsCheckedResult | undefined>;

export type UpdateSourceType = "pending" | "ignored";

export interface UpdateFromData {
  serverUrl: string;
  kbName: string;
  versionName: string;
  lastFullUpdate: Date;
}

export interface MessageData {
  type: "info" | "success" | "warning" | "error";
  text: string;
}
